import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { METHODS, SERVICES } from '../../api/constants';
import { commonMoodlePayload } from '../../config';
import { makeRequest } from '../../api/api';
import { MOODLE_BACKEND_ROUTES } from '../../config/apiRoutes';
import { customToast } from '../../constant/constant';

const getTimePlus30MinutesIST = () => {
  const currentDate = new Date();
  currentDate.setMinutes(currentDate.getMinutes() + 30);
  const istOffset = 5.5 * 60 * 60 * 1000;
  const istTime = new Date(currentDate.getTime() + istOffset);
  const hours = istTime.getUTCHours();
  const minutes = istTime.getUTCMinutes();
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const formattedHours = hours % 12 || 12;
  const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;

  return `${formattedHours}:${formattedMinutes} ${ampm}`;
};

export const fetchForumDiscussion = createAsyncThunk(
  'forumActivitySlice/fetchForumDiscussion',
  async (param, thunkApi) => {
    try {
      const response = await makeRequest(
        SERVICES.MOODLE_SERVICE,
        '',
        METHODS.GET,
        {
          ...param,
          ...commonMoodlePayload,
          wsfunction: MOODLE_BACKEND_ROUTES.GET_FORUM_DISCUSSIONS,
          wstoken: thunkApi.getState().auth.token
        }
      );

      return response.data;
    } catch (e) {
      thunkApi.rejectWithValue(e);
    }
  }
);

export const createForumDiscussion = createAsyncThunk(
  'forumActivitySlice/createForumDiscussion',
  async (param, thunkApi) => {
    try {
      const response = await makeRequest(
        SERVICES.MOODLE_SERVICE,
        '',
        METHODS.GET,
        {
          ...param,
          ...commonMoodlePayload,
          wsfunction: MOODLE_BACKEND_ROUTES.ADD_DISCUSSION_IN_FORUM,
          wstoken: thunkApi.getState().auth.token
        }
      );

      if (
        response.status == 200 &&
        !(response?.data?.errorcode || response?.data?.exception)
      ) {
        thunkApi.dispatch(fetchForumDiscussion({ forumid: param?.forumid }));
      }

      if (
        response.status == 200 &&
        !(response?.data?.errorcode || response?.data?.exception)
      ) {
        customToast(
          'Post Created Successfully',
          `You have till ${getTimePlus30MinutesIST()} to edit your post.`,
          'success',
          3000
        );
      } else {
        customToast(
          'Post Not Created',
          'This forum is no longer active',
          'error',
          3000
        );
      }
      return response.data;
    } catch (e) {
      thunkApi.rejectWithValue(e);
    }
  }
);

export const fetchForumThread = createAsyncThunk(
  'forumActivitySlice/fetchForumThread',
  async (param, thunkApi) => {
    try {
      const response = await makeRequest(
        SERVICES.MOODLE_SERVICE,
        '',
        METHODS.GET,
        {
          ...param,
          ...commonMoodlePayload,
          wsfunction: MOODLE_BACKEND_ROUTES.GET_FORUM_THREADS,
          wstoken: thunkApi.getState().auth.token
        }
      );

      return response.data;
    } catch (e) {
      thunkApi.rejectWithValue(e);
    }
  }
);

export const forumStateUpdation = createAsyncThunk(
  'forumActivitySlice/forumStateUpdation',
  async (param, thunkApi) => {
    try {
      const response = await makeRequest(
        SERVICES.MOODLE_SERVICE,
        '',
        METHODS.GET,
        {
          ...param?.data,
          ...commonMoodlePayload,
          wstoken: thunkApi.getState().auth.token
        }
      );

      if (
        response.status == 200 &&
        !(response?.data?.errorcode || response?.data?.exception)
      ) {
        thunkApi.dispatch(fetchForumThread(param?.extras));
      }
      return response.data;
    } catch (e) {
      thunkApi.rejectWithValue(e);
    }
  }
);

export const forumActivitySlice = createSlice({
  name: 'forumActivitySlice',
  initialState: {
    isDiscussionLoading: false,
    iscreateDiscussionLoading: false,
    isForumStateUpdating: false,
    forumDiscussionError: false,
    discussions: [],
    error: '',
    fetchForumThread: {
      isForumThreadLoading: false,
      forumThreadsData: {},
      error: ''
    }
  },
  reducers: {
    setActivityData: (state, action) => {
      Object.keys(action.payload).map((val) => {
        state[val] = action.payload[val];
      });
    }
  },
  extraReducers: (builder) => {
    builder.addCase(fetchForumDiscussion.pending, (state) => {
      state.isDiscussionLoading = true;
    });
    builder.addCase(fetchForumDiscussion.fulfilled, (state, action) => {
      state.isDiscussionLoading = false;
      state.discussions = action?.payload?.discussions || [];
      state.forumDiscussionError =
        action?.payload?.discussions?.length > 0 ? false : true;
    });
    builder.addCase(fetchForumDiscussion.rejected, (state) => {
      state.isDiscussionLoading = false;
    });
    ///////////////////////////////////////////////////////////////////////////
    builder.addCase(createForumDiscussion.pending, (state) => {
      state.iscreateDiscussionLoading = true;
    });
    builder.addCase(createForumDiscussion.fulfilled, (state, action) => {
      state.iscreateDiscussionLoading = false;
    });
    builder.addCase(createForumDiscussion.rejected, (state) => {
      state.iscreateDiscussionLoading = false;
    });
    ////////////////////////////////////////////////////////////////////////////
    builder.addCase(fetchForumThread.pending, (state) => {
      state.fetchForumThread.isForumThreadLoading = true;
    });
    builder.addCase(fetchForumThread.fulfilled, (state, action) => {
      state.fetchForumThread.isForumThreadLoading = false;
      state.fetchForumThread.forumThreadsData = action?.payload || {};
    });
    builder.addCase(fetchForumThread.rejected, (state) => {
      state.fetchForumThread.isForumThreadLoading = false;
    });
    ///////////////////////////////////////////////////////////////////////////////
    builder.addCase(forumStateUpdation.pending, (state) => {
      state.isForumStateUpdating = true;
    });
    builder.addCase(forumStateUpdation.fulfilled, (state, action) => {
      state.isForumStateUpdating = false;
    });
    builder.addCase(forumStateUpdation.rejected, (state) => {
      state.isForumStateUpdating = false;
    });
  }
});

export const { setActivityData } = forumActivitySlice.actions;

export default forumActivitySlice.reducer;
